import React, { useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import {
  AiOutlineUser,
  AiOutlineLock,
} from "react-icons/ai";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import axios from "axios";
import { useDispatch } from "react-redux";
import { loginSuccess } from "../redux-toolkit/slice/authSlice";
import Captcha from "../component/Captcha";
import loaderimg from "../Images/loader1.gif";
import financialChartBg from "../Images/financial_chart_bg.png";
import cityscapeChartBg from "../Images/cityscape_chart_bg.png";
import { API_BASE_URL } from "../config/apiConfig";
import { useBranding } from "../context/BrandingContext";

const LoginPage = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const branding = useBranding();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const captchaRef = useRef();

  React.useEffect(() => {
    const logoutMessage = localStorage.getItem("logout_message");
    if (logoutMessage) {
      toast.error(logoutMessage, {
        toastId: 'multi-device-logout-alert',
        autoClose: 6000
      });
      localStorage.removeItem("logout_message");
    }
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    if (!captchaRef.current.verifyCaptcha()) {
      setError("CAPTCHA verification failed. Please try again.");
      captchaRef.current.regenerateCaptcha();
      setLoading(false);
      return;
    }

    try {
      const url = `${API_BASE_URL}/api/Accounts/auth_user`;
      const data = { email, password };

      const response = await axios.post(url, data);
      const body = response.data || {};

      // The API answers 200 even when the credentials are rejected - the real
      // outcome is in the body (200 ok, 201 unknown user, 202 bad password,
      // 203 inactive). Without this check a failed login used to dispatch
      // loginSuccess with no token and land the user on /no-access.
      const resultCode = Number(body.statusCode ?? body.StatusCode ?? 200);
      const token = body.token || body.accessToken;

      if (resultCode !== 200 || !token) {
        const reason =
          body.status || body.Status || "Login failed. Please check your details.";
        setError(reason);
        toast.error(reason);
        captchaRef.current.regenerateCaptcha();
        setLoading(false);
        return;
      }

      sessionStorage.setItem("authToken", token);

      dispatch(loginSuccess(body));
      toast.success(body.status || "Signed in.");

      const lastVisitedPage = sessionStorage.getItem("lastVisitedPage");
      if (lastVisitedPage) {
        navigate(lastVisitedPage, { replace: true });
        sessionStorage.removeItem("lastVisitedPage");
      } else {
        // Where a role lands is configured on the role itself (Role_Master.
        // landing_route), so a new role needs no change here.
        //
        // But only send them there if it is actually in their menu. A role whose
        // landing page has been revoked would otherwise drop every one of its
        // users on /no-access at sign-in, with no way forward.
        const menu = body.menu || [];
        const permitted = new Set(menu.map((m) => m.routePath).filter(Boolean));

        const firstRoute =
          menu.find((m) => m.routePath && !m.parentModuleId)?.routePath ||
          menu.find((m) => m.routePath)?.routePath;

        const landing =
          body.landingRoute && permitted.has(body.landingRoute)
            ? body.landingRoute
            : firstRoute;

        navigate(landing || "/no-access", { replace: true });
      }
    } catch (err) {
      // err.response is absent when the request never reached the API - a CORS
      // rejection, the wrong REACT_APP_API_BASE_URL, an untrusted dev
      // certificate, or the API simply not running. Saying so beats a generic
      // "login failed", which sends people hunting for a password problem.
      let message;

      if (!err.response) {
        message =
          `Could not reach the API at ${API_BASE_URL}. Check that it is running, ` +
          `that this origin (${window.location.origin}) is listed in Cors:AllowedOrigins, ` +
          `and that its certificate is trusted.`;
      } else {
        const d = err.response.data;
        message =
          (typeof d === "string" && d) ||
          d?.status ||
          d?.Status ||
          d?.title ||
          `Login failed (HTTP ${err.response.status}).`;
      }

      setError(message);
      toast.error(message);
      captchaRef.current.regenerateCaptcha();
    } finally {
      setLoading(false);
    }
  };

  const [isForgotOpen, setIsForgotOpen] = useState(false);
  const [fpStep, setFpStep] = useState(1);
  const [fpEmail, setFpEmail] = useState("");
  const [fpOtp, setFpOtp] = useState("");
  const [storedOtp, setStoredOtp] = useState("");
  const [fpNewPassword, setFpNewPassword] = useState("");
  const [fpConfirmPassword, setFpConfirmPassword] = useState("");
  const [fpLoading, setFpLoading] = useState(false);

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const OTP_API_URL = `${API_BASE_URL}/api/NewUser/Send_Otp_email`;
  const RESET_PASS_API_URL = `${API_BASE_URL}/api/NewUser/Forget_pass`;

  const openForgotModal = () => {
    setIsForgotOpen(true);
    setFpStep(1);
    setFpEmail(email || "");
    setFpOtp("");
    setStoredOtp("");
    setFpNewPassword("");
    setFpConfirmPassword("");
  };

  const closeForgotModal = () => {
    setIsForgotOpen(false);
  };

  const handleSendOtp = async () => {
    if (!fpEmail) {
      toast.error("Please enter your email.");
      return;
    }
    try {
      setFpLoading(true);
      const url = `${OTP_API_URL}?email=${encodeURIComponent(fpEmail)}`;
      const res = await axios.post(url);
      if (res.status === 200) {
        const otpFromServer = res.data?.email_Otp;
        if (!otpFromServer) {
          toast.error("Unable to read OTP from server response.");
          return;
        }
        setStoredOtp(otpFromServer);
        localStorage.setItem("forgotPasswordOTP", otpFromServer);
        localStorage.setItem("forgotPasswordEmail", fpEmail);
        toast.success(res.data?.status || `OTP sent to ${fpEmail}`);
        setFpStep(2);
      } else {
        toast.error(`Failed to send OTP. Status: ${res.status}`);
      }
    } catch (error) {
      console.error("Send OTP error:", error);
      const msg = error.response?.data?.status || error.response?.data || "Failed to send OTP. Please try again.";
      toast.error(msg);
    } finally {
      setFpLoading(false);
    }
  };

  const handleVerifyOtp = () => {
    const localOtp = storedOtp || localStorage.getItem("forgotPasswordOTP");
    if (!fpOtp) {
      toast.error("Please enter the OTP.");
      return;
    }
    if (!localOtp) {
      toast.error("No OTP found. Please request a new OTP.");
      setFpStep(1);
      return;
    }
    if (fpOtp.trim() !== localOtp.trim()) {
      toast.error("Incorrect OTP. Please try again.");
      return;
    }
    toast.success("OTP verified successfully.");
    setFpStep(3);
  };

  const handleChangePassword = async () => {
    if (!fpNewPassword || !fpConfirmPassword) {
      toast.error("Please enter both password fields.");
      return;
    }
    if (fpNewPassword !== fpConfirmPassword) {
      toast.error("Passwords do not match.");
      return;
    }
    try {
      setFpLoading(true);
      const payload = {
        email: fpEmail,
        password: fpNewPassword,
        confPassword: fpConfirmPassword,
      };
      const res = await axios.put(RESET_PASS_API_URL, payload, {
        headers: { "Content-Type": "application/json" },
      });
      if (res.status === 200) {
        const statusMsg = res.data?.status || "Password changed successfully.";
        toast.success(statusMsg);
        localStorage.removeItem("forgotPasswordOTP");
        localStorage.removeItem("forgotPasswordEmail");
        closeForgotModal();
        setPassword("");
      } else {
        toast.error(`Failed to change password. Status: ${res.status}`);
      }
    } catch (error) {
      console.error("Reset password error:", error);
      const msg = error.response?.data?.status || error.response?.data || "Failed to change password. Please try again.";
      toast.error(msg);
    } finally {
      setFpLoading(false);
    }
  };

  return (
    <div className="relative min-h-screen flex items-center justify-center bg-gradient-to-tr from-[#E0F2FE] via-[#F8FAFC] to-[#EFF6FF] py-4 px-4 sm:px-6 lg:px-8 overflow-hidden font-sans select-none">
      {/* Exact Visual Mockup Waves and Background */}
      {/* Top Left Organic Shape */}
      <div className="absolute top-0 left-0 w-[420px] h-[320px] bg-gradient-to-br from-[#0EA5E9] to-[#0284C7] rounded-br-[18rem] z-0 opacity-80 select-none"></div>

      {/* Top Right Curved Flowing Waves */}
      <div className="absolute top-0 right-0 w-[450px] h-[350px] bg-gradient-to-bl from-[#1D4ED8] to-[#2563EB] rounded-bl-[16rem] z-0 opacity-95 select-none overflow-hidden">
        <div className="absolute top-1/4 right-1/4 w-72 h-72 bg-white/10 rounded-full border border-white/10"></div>
      </div>

      {/* Curved Dynamic Blue Stroke/Lines */}
      <svg className="absolute inset-0 w-full h-full z-0 select-none pointer-events-none" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1440 900" preserveAspectRatio="none">
        <path d="M-50,250 C300,120 550,680 1450,220" fill="none" stroke="#BAE6FD" strokeWidth="5" opacity="0.5" />
        <path d="M-10,420 C400,220 850,780 1500,320" fill="none" stroke="#E0F2FE" strokeWidth="3" opacity="0.7" />
      </svg>

      {/* Bottom Right Solid Wave with Star/Cross */}
      <div className="absolute bottom-0 right-0 w-[420px] h-[350px] bg-gradient-to-tl from-[#0284C7] to-[#38BDF8] rounded-tl-[14rem] flex items-center justify-center z-0 opacity-95 overflow-hidden select-none pointer-events-none">
        <div className="absolute bottom-16 right-16 text-white text-5xl opacity-40 animate-pulse select-none">✦</div>
      </div>

      {loading && (
        <div className="absolute inset-0 bg-white/60 backdrop-blur-md z-50 flex flex-col gap-3 items-center justify-center">
          <img src={loaderimg} alt="Loading" className="w-16 h-16 animate-spin" />
          <p className="text-sm font-black text-slate-600 tracking-wide">Authenticating...</p>
        </div>
      )}

      {/* Forgot Password Modal */}
      {isForgotOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center animate-fade-in">
          <div className="absolute inset-0 bg-black/30 backdrop-blur-sm" onClick={closeForgotModal} />
          <div className="relative bg-white rounded-3xl shadow-2xl w-full max-w-md mx-4 overflow-hidden border border-slate-100 z-50 animate-scale-up">
            <div className="bg-gradient-to-r from-sky-500 to-indigo-500 px-6 py-5 flex items-center justify-between">
              <div>
                <h2 className="text-lg font-bold text-white tracking-wide">Forgot Password</h2>
                <p className="text-xs text-white/80">We'll help you securely reset it</p>
              </div>
              <button onClick={closeForgotModal} className="text-white/80 hover:text-white text-2xl outline-none transition-colors">×</button>
            </div>

            <div className="px-6 py-6 space-y-5 bg-white">
              <div className="flex items-center justify-center gap-3 text-xs font-bold text-slate-400">
                <span className={`flex items-center gap-1 ${fpStep === 1 ? "text-sky-600" : "text-slate-400"}`}>
                  <span className={`w-6 h-6 flex items-center justify-center rounded-full border ${fpStep === 1 ? "bg-sky-500 border-sky-500 text-white" : "border-slate-300"}`}>1</span> Email
                </span>
                <span className="h-px w-6 bg-slate-200" />
                <span className={`flex items-center gap-1 ${fpStep === 2 ? "text-sky-600" : "text-slate-400"}`}>
                  <span className={`w-6 h-6 flex items-center justify-center rounded-full border ${fpStep === 2 ? "bg-sky-500 border-sky-500 text-white" : "border-slate-300"}`}>2</span> OTP
                </span>
                <span className="h-px w-6 bg-slate-200" />
                <span className={`flex items-center gap-1 ${fpStep === 3 ? "text-sky-600" : "text-slate-400"}`}>
                  <span className={`w-6 h-6 flex items-center justify-center rounded-full border ${fpStep === 3 ? "bg-sky-500 border-sky-500 text-white" : "border-slate-300"}`}>3</span> Password
                </span>
              </div>

              {fpStep === 1 && (
                <div className="space-y-4">
                  <div>
                    <label className="block text-xs font-bold text-slate-600 uppercase tracking-widest mb-1.5">Email Address</label>
                    <input
                      type="email"
                      value={fpEmail}
                      onChange={(e) => setFpEmail(e.target.value)}
                      className="w-full bg-slate-50 border border-slate-200 rounded-2xl px-4 py-3.5 focus:outline-none focus:bg-white focus:ring-4 focus:ring-sky-50 focus:border-sky-500 transition-all font-semibold text-sm text-slate-700"
                      placeholder="you@company.com"
                    />
                  </div>
                  <button onClick={handleSendOtp} disabled={fpLoading} className="w-full py-3.5 font-black uppercase tracking-wider text-xs rounded-2xl text-white bg-sky-500 hover:bg-sky-600 transition-all shadow-xl shadow-sky-100 disabled:opacity-50">
                    {fpLoading ? "Sending OTP..." : "Send OTP"}
                  </button>
                </div>
              )}

              {fpStep === 2 && (
                <div className="space-y-4">
                  <div>
                    <label className="block text-xs font-bold text-slate-600 uppercase tracking-widest mb-1.5">Enter OTP</label>
                    <input
                      type="text"
                      value={fpOtp}
                      onChange={(e) => setFpOtp(e.target.value)}
                      className="w-full bg-slate-50 border border-slate-200 rounded-2xl px-4 py-3.5 tracking-[0.35em] text-center font-black text-lg focus:outline-none focus:bg-white focus:ring-4 focus:ring-sky-50 focus:border-sky-500 transition-all text-slate-800"
                      placeholder="••••"
                    />
                  </div>
                  <div className="flex gap-3">
                    <button onClick={() => setFpStep(1)} className="w-1/2 py-3 font-black uppercase tracking-wider text-xs rounded-2xl border border-slate-200 bg-white hover:bg-slate-50 text-slate-600 transition-all">Back</button>
                    <button onClick={handleVerifyOtp} className="w-1/2 py-3 font-black uppercase tracking-wider text-xs rounded-2xl bg-sky-500 hover:bg-sky-600 text-white transition-all shadow-xl shadow-sky-100">Verify</button>
                  </div>
                </div>
              )}

              {fpStep === 3 && (
                <div className="space-y-4">
                  <div>
                    <label className="block text-xs font-bold text-slate-600 uppercase tracking-widest mb-1.5">New Password</label>
                    <input
                      type="password"
                      value={fpNewPassword}
                      onChange={(e) => setFpNewPassword(e.target.value)}
                      className="w-full bg-slate-50 border border-slate-200 rounded-2xl px-4 py-3.5 focus:outline-none focus:bg-white focus:ring-4 focus:ring-sky-50 focus:border-sky-500 transition-all font-semibold text-sm text-slate-700"
                      placeholder="Enter new password"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-bold text-slate-600 uppercase tracking-widest mb-1.5">Confirm Password</label>
                    <input
                      type="password"
                      value={fpConfirmPassword}
                      onChange={(e) => setFpConfirmPassword(e.target.value)}
                      className="w-full bg-slate-50 border border-slate-200 rounded-2xl px-4 py-3.5 focus:outline-none focus:bg-white focus:ring-4 focus:ring-sky-50 focus:border-sky-500 transition-all font-semibold text-sm text-slate-700"
                      placeholder="Re-enter new password"
                    />
                  </div>
                  <div className="flex gap-3">
                    <button onClick={() => setFpStep(2)} className="w-1/2 py-3 font-black uppercase tracking-wider text-xs rounded-2xl border border-slate-200 bg-white hover:bg-slate-50 text-slate-600 transition-all">Back</button>
                    <button onClick={handleChangePassword} disabled={fpLoading} className="w-1/2 py-3 font-black uppercase tracking-wider text-xs rounded-2xl bg-emerald-500 hover:bg-emerald-600 text-white transition-all shadow-xl shadow-emerald-100 disabled:opacity-50">
                      {fpLoading ? "Changing..." : "Save"}
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      <div className="">

        {/* Left Layered blurred Stock Graphic Panel */}
        <div className="hidden xl:flex flex-col justify-between w-[310px] h-[430px] bg-gradient-to-br from-blue-100/40 via-sky-50/40 to-indigo-100/30 backdrop-blur-md rounded-[2rem] p-5 border border-white/50 shadow-xl overflow-hidden animate-fade-in relative flex-shrink-0 select-none">
          <div className="absolute inset-0 bg-cover bg-center rounded-[2rem] opacity-75 hover:scale-105 duration-700 transition-all" style={{ backgroundImage: `url(${financialChartBg})` }}></div>
          <div className="relative z-10 flex flex-col h-full justify-between">
            <div className="flex items-center gap-1.5 opacity-85">
              <span className="w-3 h-3 bg-red-400 rounded-full shadow-md"></span>
              <span className="w-3 h-3 bg-yellow-400 rounded-full shadow-md"></span>
              <span className="w-3 h-3 bg-green-400 rounded-full shadow-md"></span>
            </div>
            <div className="p-3.5 bg-white/50 backdrop-blur-lg border border-white/40 rounded-[1.5rem] shadow-lg flex flex-col gap-1.5">
              <div className="flex items-center justify-between text-xs font-black text-sky-800 uppercase tracking-wider">
                <span>Real-time Insights</span>
                <span className="text-[10px] bg-emerald-100 text-emerald-700 px-2 py-0.5 rounded-full font-bold">+28%</span>
              </div>
              <div className="flex gap-1 h-12 items-end mt-1">
                <div className="w-full bg-sky-500 rounded-t h-4 opacity-75"></div>
                <div className="w-full bg-sky-500 rounded-t h-10 opacity-80"></div>
                <div className="w-full bg-sky-500 rounded-t h-7 opacity-85"></div>
                <div className="w-full bg-sky-500 rounded-t h-12 opacity-90"></div>
                <div className="w-full bg-sky-500 rounded-t h-6 opacity-95"></div>
                <div className="w-full bg-indigo-500 rounded-t h-14"></div>
              </div>
            </div>
          </div>
        </div>

        {/* Central Magnificently Crafted Premium White Login Card */}
        <div className="relative z-20 bg-white rounded-[2rem] p-6 md:p-7 w-full max-w-[400px] shadow-2xl flex flex-col gap-4 border border-slate-100/80 flex-shrink-0 animate-scale-up">
          {/* Brand - resolved at runtime from App_Settings, never hard-coded */}
          <div className="flex flex-col items-center justify-center gap-1 mb-1">
            <div className="flex items-center justify-center gap-1 select-none">
              {branding.loginLogoUrl ? (
                <img
                  src={branding.loginLogoUrl}
                  alt={branding.appName}
                  className="w-24 h-24 object-contain"
                />
              ) : (
                <div
                  className="w-20 h-20 rounded-3xl flex items-center justify-center text-white text-3xl font-black shadow-lg"
                  style={{ backgroundColor: branding.accentColor }}
                >
                  {branding.initials}
                </div>
              )}
            </div>
            <h1 className="text-2xl md:text-3xl font-bold text-[#111827] tracking-tight text-center">
              {branding.appName}
            </h1>
            <p className="text-xs md:text-sm text-slate-500 tracking-wide text-center mt-0.5">
              {branding.tagline || "Welcome back. Sign in to continue."}
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-5">
            <div className="space-y-3">
              {/* Username field */}
              <div className="relative flex items-center bg-sky-50/40 border border-sky-100 rounded-2xl px-4 py-2.5 focus-within:border-sky-400 focus-within:bg-white focus-within:ring-4 focus-within:ring-sky-50 transition-all duration-300">
                <AiOutlineUser className="text-sky-500 text-xl mr-3 flex-shrink-0" />
                <input
                  id="email-address"
                  name="email"
                  type="email"
                  autoComplete="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Username or Email Address"
                  className="bg-transparent text-sm  text-slate-700 placeholder-slate-400 outline-none w-full"
                  disabled={loading}
                />
              </div>

              {/* Password field */}
              <div className="relative flex items-center bg-sky-50/40 border border-sky-100 rounded-2xl px-4 py-2.5 focus-within:border-sky-400 focus-within:bg-white focus-within:ring-4 focus-within:ring-sky-50 transition-all duration-300">
                <AiOutlineLock className="text-sky-500 text-xl mr-3 flex-shrink-0" />
                <input
                  id="password"
                  name="password"
                  type={showPassword ? "text" : "password"}
                  autoComplete="current-password"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Password"
                  className="bg-transparent text-sm  text-slate-700 placeholder-slate-400 outline-none w-full"
                  disabled={loading}
                />
                <button
                  type="button"
                  onClick={togglePasswordVisibility}
                  className="text-xs  text-sky-600 hover:text-sky-800 tracking-wider select-none outline-none pl-2 flex-shrink-0 transition-colors"
                >
                  {showPassword ? "Hide" : "Show"}
                </button>
              </div>

              {/* Captcha Section */}
              <Captcha ref={captchaRef} />

              {/* Status Message */}
              {error && (
                <div className="p-3 bg-red-50/70 border border-red-100/80 rounded-2xl text-xs font-bold text-red-600 animate-fade-in text-center leading-relaxed">
                  {error}
                </div>
              )}

              {/* Sign In button */}
              <div className="pt-1">
                <button
                  type="submit"
                  className="w-full bg-[#1F2937] hover:bg-[#111827] bg-gradient-to-r from-sky-500 to-sky-600 hover:from-sky-600 hover:to-sky-700 text-white font-black text-xs uppercase tracking-widest py-3 px-4 rounded-2xl shadow-xl shadow-sky-100 hover:shadow-sky-200 transition-all active:scale-95 duration-200 flex items-center justify-center outline-none select-none"
                  disabled={loading}
                >
                  {loading ? "Signing in..." : "Sign In"}
                </button>
              </div>

              {/* Extra links row */}
              <div className="flex items-center justify-between text-xs  px-1 select-none pt-1">
                {/* <label className="flex items-center gap-2 text-slate-600 cursor-pointer hover:text-slate-800 transition-colors">
                  <input type="checkbox" className="w-3.5 h-3.5 rounded accent-sky-500 bg-slate-50 border border-slate-200" />
                  Remember Me
                </label> */}
                <button
                  type="button"
                  onClick={openForgotModal}
                  className="text-sky-600 hover:text-sky-800 transition-all tracking-wide select-none outline-none"
                >
                  Forgot Password?
                </button>
              </div>
            </div>
          </form>

          {/* New User Footer */}
          {/* <div className="text-center mt-1 border-t border-slate-50 pt-3">
            <p className="text-xs font-bold text-slate-400">
              New user?{" "}
              <span onClick={() => navigate("/Register")} className="text-sky-600 hover:text-sky-800 cursor-pointer hover:underline transition-all">Sign Up</span>
            </p>
          </div> */}
        </div>

        {/* Right Layered blurred Stock City Graphic Panel */}
        <div className="hidden xl:flex flex-col justify-between w-[310px] h-[430px] bg-gradient-to-br from-indigo-100/40 via-sky-50/40 to-blue-100/30 backdrop-blur-md rounded-[2rem] p-5 border border-white/50 shadow-xl overflow-hidden animate-fade-in relative flex-shrink-0 select-none">
          <div className="absolute inset-0 bg-cover bg-center rounded-[2rem] opacity-75 hover:scale-105 duration-700 transition-all" style={{ backgroundImage: `url(${cityscapeChartBg})` }}></div>
          <div className="relative z-10 flex flex-col h-full justify-between">
            <div className="flex items-center gap-1.5 opacity-85 justify-end">
              <span className="w-3 h-3 bg-sky-400 rounded-full shadow-md"></span>
              <span className="w-3 h-3 bg-indigo-400 rounded-full shadow-md"></span>
            </div>
            <div className="p-3.5 bg-white/50 backdrop-blur-lg border border-white/40 rounded-[1.5rem] shadow-lg flex flex-col gap-1.5">
              <div className="text-xs font-black text-slate-800 uppercase tracking-wider">City Trends</div>
              <p className="text-[10px] font-bold text-slate-500 leading-relaxed uppercase tracking-wider">Financial Centers Growing</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
